import React, { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { useAuth, canEditTallyCredentials } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";

export default function AccountSettingsDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const showTally = canEditTallyCredentials(user);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [tallyUsername, setTallyUsername] = useState("");
  const [tallyPassword, setTallyPassword] = useState("");
  const [clearTallyPassword, setClearTallyPassword] = useState(false);
  const [savingTally, setSavingTally] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setTallyUsername(user?.tally_username || "");
    setTallyPassword("");
    setClearTallyPassword(false);
  }, [open, user?.id, user?.tally_username]);

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSavingPassword(true);
    try {
      const { data } = await api.patch("/auth/me/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setUser(data);
      toast.success("Password updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSavingPassword(false);
    }
  };

  const saveTally = async (e) => {
    e.preventDefault();
    setSavingTally(true);
    try {
      const { data } = await api.patch("/auth/me/tally-credentials", {
        tally_username: tallyUsername,
        tally_password: clearTallyPassword ? undefined : tallyPassword || undefined,
        clear_tally_password: clearTallyPassword,
      });
      setUser(data);
      toast.success("Tally credentials saved");
      setTallyPassword("");
      setClearTallyPassword(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSavingTally(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="account-settings-dialog">
        <DialogHeader>
          <DialogTitle>Account settings</DialogTitle>
          <DialogDescription>
            Change your ERP sign-in password
            {showTally ? " and Tally company-security login used for sync." : "."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={savePassword} className="space-y-3 border-b border-border pb-4">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">ERP password</p>
          <div className="space-y-1.5">
            <Label htmlFor="acct-current-password">Current password</Label>
            <Input
              id="acct-current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acct-new-password">New password</Label>
            <Input
              id="acct-new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={6}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acct-confirm-password">Confirm new password</Label>
            <Input
              id="acct-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={6}
              required
            />
          </div>
          <DialogFooter className="pt-1">
            <Button type="submit" disabled={savingPassword} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
              {savingPassword ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>

        {showTally ? (
          <form onSubmit={saveTally} className="space-y-3 pt-1">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Tally sync login</p>
            <p className="text-xs text-muted-foreground">
              Company-security user shown in Tally Edit Log when you Approve &amp; Sync. Each accounts user should use their own ID.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="acct-tally-user">Tally user ID</Label>
              <Input
                id="acct-tally-user"
                value={tallyUsername}
                onChange={(e) => setTallyUsername(e.target.value)}
                autoComplete="off"
                placeholder="Tally username"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acct-tally-password">
                Tally password{user?.tally_password_configured ? " (leave blank to keep)" : ""}
              </Label>
              <Input
                id="acct-tally-password"
                type="password"
                autoComplete="new-password"
                value={tallyPassword}
                onChange={(e) => {
                  setTallyPassword(e.target.value);
                  if (e.target.value) setClearTallyPassword(false);
                }}
                disabled={clearTallyPassword}
                placeholder={user?.tally_password_configured ? "••••••••" : ""}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={clearTallyPassword}
                onChange={(e) => {
                  setClearTallyPassword(e.target.checked);
                  if (e.target.checked) setTallyPassword("");
                }}
              />
              Clear saved Tally password
            </label>
            <DialogFooter>
              <Button type="submit" disabled={savingTally} variant="outline">
                {savingTally ? "Saving…" : "Save Tally login"}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
